import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Gamepad2, Landmark, Sparkles, Zap } from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  MediaCarousel,
  PodcastCard,
  VideoCard,
} from "../shared/categoryDetailShared";

const easeOut = [0.22, 1, 0.36, 1];

function BettingSectionHead({ eyebrow, title, subtitle }) {
  return (
    <div className="relative mb-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <span className="h-px w-8 bg-gradient-to-r from-orange-500 to-[#FFD221]" aria-hidden />
        <span className="text-[11px] font-black uppercase tracking-[0.28em] text-orange-600">{eyebrow}</span>
      </div>
      <h2 className="mt-3 text-2xl font-black leading-[1.15] tracking-tight text-stone-950 sm:text-3xl lg:text-[2.05rem]">
        {title}
      </h2>
      {subtitle ? <p className="mt-2 max-w-2xl text-sm font-semibold text-stone-600">{subtitle}</p> : null}
    </div>
  );
}

function CheckList({ items, color }) {
  return (
    <ul className="space-y-2.5">
      {items.map((line) => (
        <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-stone-800 sm:text-[0.9375rem]">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color }} strokeWidth={2.2} />
          <span>{line}</span>
        </li>
      ))}
    </ul>
  );
}

function SurfaceCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-orange-200/40 bg-white p-6 shadow-[0_20px_55px_-40px_rgba(234,88,12,0.22)] ring-1 ring-orange-950/[0.04] sm:p-8 ${className}`.trim()}
    >
      {children}
    </div>
  );
}

function CasinoOverviewKickoff({ data }) {
  const k = data.introKickoff;
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: easeOut }}
      className="overflow-hidden rounded-[1.75rem] border border-stone-200/80 bg-white shadow-[0_24px_80px_-40px_rgba(15,23,42,0.14)] sm:rounded-[2rem]"
    >
      <div className="grid lg:grid-cols-2 lg:items-stretch">
        <div className="relative flex flex-col justify-center overflow-hidden px-7 py-10 sm:px-10 sm:py-12 lg:px-12 lg:py-14">
          <div
            className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full opacity-[0.055] blur-3xl"
            style={{ background: "#1c1917" }}
            aria-hidden
          />
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-white">
              {k.chipCategory}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200/80 bg-orange-50/80 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-orange-700">
              <Gamepad2 className="h-3 w-3 shrink-0" />
              {k.chipSecondary}
            </span>
          </div>
          <h2 className="text-[1.65rem] font-black leading-[1.1] tracking-tight text-stone-950 sm:text-[2rem] lg:text-[2.1rem]">
            {k.headline}
          </h2>
          <p className="mt-4 max-w-prose text-[0.9375rem] leading-relaxed text-stone-500">{k.lead}</p>
          <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <div className="rounded-2xl border border-stone-100 bg-stone-50/80 p-4">
              <p className="text-[9px] font-black uppercase tracking-[0.24em] text-orange-600">The games</p>
              <ul className="mt-2.5 space-y-1.5">
                {k.everydayLabels.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-[0.8125rem] font-semibold text-stone-800">
                    <Zap className="h-3 w-3 shrink-0 text-orange-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-stone-100 bg-stone-50/80 p-4">
              <p className="text-[9px] font-black uppercase tracking-[0.24em] text-orange-600">In Ghana</p>
              <ul className="mt-2.5 space-y-1.5">
                {k.ghanaContext.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-[0.8125rem] font-semibold text-stone-800">
                    <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" strokeWidth={2.5} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-5 text-[0.8rem] leading-relaxed text-stone-400">{k.researchNote}</p>
          <div className="mt-5 flex flex-wrap gap-1.5">
            {k.focusTags.map((tag) => (
              <span
                key={tag}
                className="rounded-lg border border-stone-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-600 shadow-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="relative flex min-h-0 flex-col border-t border-stone-100 lg:min-h-full lg:border-l lg:border-t-0">
          <div className="absolute inset-0 bg-stone-950" />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
            aria-hidden
          />
          <div className="pointer-events-none absolute -right-12 -top-12 h-56 w-56 rounded-full bg-orange-500/20 blur-3xl" aria-hidden />
          <div className="pointer-events-none absolute -bottom-10 -left-10 h-48 w-48 rounded-full bg-[#FFD221]/12 blur-3xl" aria-hidden />

          <div className="relative flex flex-1 flex-col justify-center px-5 pb-2 pt-8 sm:px-8 sm:pb-3 sm:pt-10 lg:min-h-0 lg:px-8 lg:pt-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-24px" }}
              transition={{ duration: 0.5, ease: easeOut }}
              className="flex min-h-[min(52vh,22rem)] w-full flex-1 flex-col sm:min-h-[min(48vh,26rem)] lg:min-h-0"
            >
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex min-h-0 flex-1 flex-col rounded-[1.2rem] border-[5px] border-white bg-white p-1 shadow-[0_28px_70px_-25px_rgba(0,0,0,0.55)] ring-1 ring-white/15 sm:rounded-[1.35rem] sm:border-[6px] sm:p-1.5">
                  <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl bg-stone-200">
                    <img
                      src={data.galleryImages?.[0] ?? data.heroImage}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="relative shrink-0 border-t border-white/[0.07] px-5 py-5 sm:px-8 sm:py-6 lg:px-6">
            <p className="mb-3 text-[9px] font-black uppercase tracking-[0.28em] text-white/40">At a glance</p>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {data.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-white/[0.08] bg-white/[0.06] px-2 py-3 text-center backdrop-blur-sm sm:px-3"
                >
                  <p
                    className={`text-base font-black tabular-nums sm:text-lg ${String(stat.value).includes("#") ? "text-orange-400" : "text-white"}`}
                  >
                    {stat.value}
                  </p>
                  <p className="mt-0.5 text-[8px] font-bold uppercase leading-tight tracking-wide text-white/40 sm:text-[9px]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-center text-[10px] font-medium text-white/28">
              Educational — 18+; licensed operators only; gamble responsibly.
            </p>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function QnaBlock({ block, color }) {
  const header = (
    <div className="flex flex-wrap items-center gap-4 border-b border-orange-100/90 bg-gradient-to-r from-orange-500/10 via-white to-transparent px-6 py-5 sm:px-8">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-900 text-lg font-black text-[#FFD221] shadow-lg">
        ?
      </span>
      <h3 className="flex-1 text-lg font-black leading-snug text-stone-950 sm:text-xl">{block.question}</h3>
    </div>
  );

  const body = (() => {
    switch (block.id) {
      case "best-casino":
        return (
          <>
            <p className="text-sm font-semibold text-stone-700">{block.intro}</p>
            <p className="mt-4 text-xs font-black uppercase text-orange-600">{block.compareTitle}</p>
            <div className="mt-2">
              <CheckList items={block.compare} color={color} />
            </div>
            <p className="mt-6 text-xs font-black uppercase text-stone-600">{block.operatorsTitle}</p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {block.operators.map((o) => (
                <li key={o} className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-bold text-stone-800">
                  {o}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-stone-500">{block.operatorsDisclaimer}</p>
            <p className="mt-8 text-sm font-black text-stone-950">✔ {block.lookForTitle}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {block.lookFor.map((row) => (
                <div key={row.title} className="rounded-xl border border-stone-100 bg-stone-50/90 p-4">
                  <p className="text-xs font-black text-orange-700">{row.title}</p>
                  <p className="mt-2 text-sm font-medium text-stone-700">{row.body}</p>
                </div>
              ))}
            </div>
          </>
        );
      case "slots-rtp":
        return (
          <>
            <p className="rounded-xl bg-amber-50 px-4 py-2 text-sm font-black text-amber-950">👉 {block.truth}</p>
            <p className="text-sm font-semibold text-stone-700">{block.rngIntro}</p>
            <p className="mt-2 font-mono text-sm font-black text-stone-900">{block.rng}</p>
            <p className="text-sm text-stone-600">{block.rngMeans}</p>
            <p className="mt-6 text-xs font-black text-emerald-800">{block.understandTitle}</p>
            <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
              <p className="text-sm font-black text-emerald-950">🎰 {block.rtpTitle}</p>
              <p className="mt-2 text-sm font-medium text-stone-800">{block.rtpLead}</p>
              <p className="mt-2 text-sm font-semibold text-stone-700">{block.rtpMeans}</p>
            </div>
            <p className="mt-6 text-xs font-black uppercase text-red-800">{block.butTitle}</p>
            <p className="text-sm font-bold text-red-950">⚠ {block.but}</p>
          </>
        );
      case "casino-bonuses":
        return (
          <>
            <p className="text-sm font-semibold text-stone-700">{block.lead}</p>
            <p className="mt-6 text-xs font-black text-orange-800">{block.typesTitle}</p>
            <ul className="mt-3 space-y-3">
              {block.types.map((t) => (
                <li key={t.title} className="rounded-xl border border-orange-100 bg-orange-50/40 px-4 py-3">
                  <p className="text-sm font-black text-stone-950">✔ {t.title}</p>
                  <ul className="mt-2 text-sm font-medium text-stone-800">
                    {t.items.map((i) => (
                      <li key={i}>· {i}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-stone-500">{block.researchNote}</p>
            <div className="mt-6 rounded-2xl border-2 border-amber-300/80 bg-amber-50/70 p-5">
              <p className="text-xs font-black text-amber-900">{block.wageringTitle}</p>
              <p className="mt-2 text-sm font-semibold text-stone-800">{block.wageringLead}</p>
              <p className="mt-3 text-sm font-black text-amber-950">{block.wageringExample}</p>
            </div>
          </>
        );
      default:
        return null;
    }
  })();

  return (
    <div className="overflow-hidden rounded-[1.5rem] border border-orange-200/50 bg-stone-50/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
      {header}
      <div className="space-y-4 p-6 sm:p-8">{body}</div>
    </div>
  );
}

function GameBlock({ b }) {
  return (
    <div className="rounded-2xl border border-stone-200/90 bg-white p-5 shadow-sm">
      <span className="text-2xl" aria-hidden>
        {b.emoji}
      </span>
      <p className="mt-2 text-sm font-black text-stone-950">{b.title}</p>
      <p className="mt-1 text-sm font-semibold text-stone-600">{b.lead}</p>
      {b.features?.length ? (
        <>
          <p className="mt-3 text-xs font-black uppercase text-orange-600">{b.featuresTitle}</p>
          <ul className="mt-1 text-sm font-medium text-stone-800">
            {b.features.map((f) => (
              <li key={f}>· {f}</li>
            ))}
          </ul>
        </>
      ) : null}
      {b.note ? <p className="mt-3 text-sm text-stone-500">{b.note}</p> : null}
      {b.popular?.length ? (
        <>
          <p className="mt-3 text-xs font-black uppercase text-emerald-800">{b.popularTitle}</p>
          <div className="mt-1">
            <CheckList items={b.popular} color="#059669" />
          </div>
        </>
      ) : null}
      {b.bets?.length ? (
        <ul className="mt-2 text-sm font-bold text-stone-800">
          {b.bets.map((x) => (
            <li key={x}>· {x}</li>
          ))}
        </ul>
      ) : null}
      {b.liveGames?.length ? (
        <>
          <p className="mt-3 text-xs font-black uppercase text-stone-600">{b.liveGamesTitle}</p>
          <ul className="mt-1 text-sm font-medium text-stone-800">
            {b.liveGames.map((g) => (
              <li key={g}>· {g}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

export const CasinoGamesOverviewTab = ({ data, color }) => (
  <div className="relative space-y-14">
    <CasinoOverviewKickoff data={data} />

    <section>
      <BettingSectionHead eyebrow="Basics" title={data.plainLanguage.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.plainLanguage.lead}</p>
        <div className="mt-4">
          <CheckList items={data.plainLanguage.devices} color={color} />
        </div>
        <p className="mt-6 text-xs font-black uppercase text-stone-600">{data.plainLanguage.gamesTitle}</p>
        <ul className="mt-2 flex flex-wrap gap-2">
          {data.plainLanguage.games.map((g) => (
            <li key={g} className="rounded-lg border border-stone-100 bg-stone-50 px-2.5 py-1 text-xs font-bold text-stone-800">
              {g}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs font-black uppercase text-orange-600">{data.plainLanguage.allowTitle}</p>
        <div className="mt-2">
          <CheckList items={data.plainLanguage.allows} color={color} />
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Growth" title={data.whyGrowing.headline} />
      <SurfaceCard>
        <div className="mt-1">
          <CheckList items={data.whyGrowing.drivers} color={color} />
        </div>
        <p className="mt-6 text-sm text-stone-600">{data.whyGrowing.closing}</p>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Law" title={data.legal.headline} />
      <SurfaceCard className="!border-emerald-200/50 !bg-gradient-to-br from-emerald-50/30 to-white">
        <p className="flex items-center gap-2 text-sm font-black text-emerald-900">
          <span aria-hidden>👉</span> {data.legal.answer}
        </p>
        <p className="mt-3 text-sm font-semibold text-stone-800">{data.legal.lead}</p>
        <p className="mt-6 text-xs font-black uppercase text-stone-600">{data.legal.regulatorTitle}</p>
        <div className="mt-3 flex items-start gap-3 rounded-xl border border-emerald-200 bg-white/90 px-4 py-3">
          <Landmark className="h-5 w-5 shrink-0 text-emerald-700" />
          <div>
            <p className="text-sm font-black text-stone-900">{data.legal.regulator}</p>
            <p className="mt-4 text-xs font-black uppercase text-orange-600">{data.legal.supervisesTitle}</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-stone-800">
              {data.legal.supervises.map((s) => (
                <li key={s}>· {s}</li>
              ))}
            </ul>
          </div>
        </div>
      </SurfaceCard>
      <div className="mt-5 rounded-2xl border-2 border-amber-200 bg-amber-50/50 p-5 sm:p-6">
        <p className="text-xs font-black uppercase text-amber-900">{data.requirements.headline}</p>
        <ul className="mt-3 space-y-2 text-sm font-bold text-stone-900">
          {data.requirements.items.map((r) => (
            <li key={r}>✔ {r}</li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Games" title={data.popularGames.headline} />
      <div className="grid gap-4 sm:grid-cols-2">
        {data.popularGames.blocks.map((b) => (
          <GameBlock key={b.title} b={b} />
        ))}
      </div>
    </section>

    <section className="space-y-8">
      <BettingSectionHead eyebrow="FAQ" title="COMMON QUESTIONS GHANAIANS ASK" />
      {data.commonQna.map((block) => (
        <QnaBlock key={block.id} block={block} color={color} />
      ))}
    </section>

    <section>
      <BettingSectionHead eyebrow="Journey" title={data.flow.headline} subtitle={data.flow.sub} />
      <div className="space-y-3">
        {data.flow.steps.map((step, i) => (
          <div key={step.title} className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-black text-orange-600">
              {i + 1}. {step.title}
            </p>
            {step.bullets?.length ? (
              <ul className="mt-2 text-sm font-semibold text-stone-700">
                {step.bullets.map((x) => (
                  <li key={x}>· {x}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Payments" title={data.momo.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.momo.lead}</p>
        <p className="mt-4 text-xs font-black uppercase text-stone-600">Popular payment methods</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {data.momo.methods.map((m) => (
            <span key={m} className="rounded-full bg-stone-900 px-3 py-1.5 text-xs font-black text-white">
              {m}
            </span>
          ))}
        </div>
        <p className="mt-6 text-sm text-stone-600">{data.momo.closing}</p>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Pitfalls" title={data.whyLose.headline} />
      <SurfaceCard className="!border-red-200/50">
        <ul className="space-y-2 text-sm font-bold text-red-950">
          {data.whyLose.reasons.map((r) => (
            <li key={r}>❌ {r}</li>
          ))}
        </ul>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Wellbeing" title={data.responsible.headline} />
      <SurfaceCard>
        <p className="text-sm text-stone-600">{data.responsible.commission}</p>
        <p className="mt-6 text-sm font-black text-amber-900">{data.responsible.riskTitle}</p>
        <ul className="mt-2 flex flex-wrap gap-2">
          {data.responsible.riskFactors.map((x) => (
            <li key={x} className="rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-1.5 text-xs font-bold text-amber-950">
              {x}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs font-black uppercase text-red-800">{data.responsible.warningsTitle}</p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-red-950">
          {data.responsible.warnings.map((w) => (
            <li key={w}>🚩 {w}</li>
          ))}
        </ul>
        <p className="mt-6 text-xs font-black uppercase text-emerald-800">{data.responsible.tipsTitle}</p>
        <div className="mt-2">
          <CheckList items={data.responsible.tips} color="#059669" />
        </div>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Scams" title={data.scams.headline} />
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border-2 border-red-200 bg-red-50/40 p-6">
          <p className="text-xs font-black uppercase text-red-900">{data.scams.scamsTitle}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-red-950">
            {data.scams.scams.map((s) => (
              <li key={s}>🚩 {s}</li>
            ))}
          </ul>
        </div>
        <SurfaceCard className="!border-emerald-200/60">
          <p className="text-xs font-black uppercase text-emerald-900">{data.scams.safeTitle}</p>
          <div className="mt-3">
            <CheckList items={data.scams.safe} color="#059669" />
          </div>
        </SurfaceCard>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Trust" title={data.security.headline} />
      <SurfaceCard>
        <p className="text-sm font-semibold text-stone-700">{data.security.lead}</p>
        <div className="mt-4">
          <CheckList items={data.security.items} color={color} />
        </div>
        <p className="mt-4 text-sm text-stone-500">{data.security.closing}</p>
      </SurfaceCard>
    </section>

    <section>
      <BettingSectionHead eyebrow="Math" title={data.rtpHouse.headline} />
      <div className="grid gap-5 lg:grid-cols-2">
        <SurfaceCard>
          <p className="text-sm font-black text-stone-950">📊 {data.rtpHouse.rtpLabel}</p>
          <p className="mt-3 text-sm font-semibold text-stone-700">{data.rtpHouse.rtpExampleIntro}</p>
          <p className="mt-2 text-sm font-semibold text-stone-800">{data.rtpHouse.rtpExampleMeans}</p>
          {data.rtpHouse.rtpExampleBullets?.length ? (
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm font-medium text-stone-700">
              {data.rtpHouse.rtpExampleBullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </SurfaceCard>
        <SurfaceCard className="!border-stone-300">
          <p className="text-sm font-black text-stone-950">{data.rtpHouse.houseTitle}</p>
          <p className="mt-3 text-sm font-semibold text-stone-700">{data.rtpHouse.house}</p>
          <p className="mt-4 flex gap-2 text-sm font-bold text-orange-800">
            <span>👉</span>
            {data.rtpHouse.closing}
          </p>
        </SurfaceCard>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Editorial" title={data.contentIdeas.headline} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.contentIdeas.groups.map((g) => (
          <div key={g.title} className="rounded-2xl border border-stone-200 bg-stone-50/80 p-5">
            <p className="text-sm font-black text-stone-950">{g.title}</p>
            <ul className="mt-3 space-y-1 text-sm font-semibold text-stone-700">
              {g.bullets.map((b) => (
                <li key={b}>· {b}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section className="overflow-hidden rounded-[1.75rem] border border-orange-400/30 bg-gradient-to-br from-[#1a1512] via-stone-900 to-orange-950 p-8 text-white shadow-2xl sm:p-10">
      <div className="flex flex-wrap items-center gap-3">
        <Sparkles className="h-8 w-8 text-[#FFD221]" />
        <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]">
          Infoboxx funnel
        </span>
      </div>
      <h3 className="mt-5 text-2xl font-black sm:text-3xl">{data.funnel.headline}</h3>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          {[
            { k: "Hook", v: data.funnel.hook },
            { k: "Education", v: data.funnel.education },
            { k: "Trust", v: data.funnel.trust },
          ].map(({ k, v }) => (
            <div key={k} className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-200">{k}</p>
              <p className="mt-2 text-sm font-semibold text-white/92">{v}</p>
            </div>
          ))}
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]">Conversion</p>
          <ul className="mt-3 space-y-2 text-sm font-bold text-white/90">
            {data.funnel.conversion.map((c) => (
              <li key={c} className="flex gap-2 rounded-xl border border-white/10 bg-black/25 px-3 py-2">
                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-[#FFD221]" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="rounded-[1.75rem] border-2 border-orange-300/60 bg-gradient-to-br from-[#FFD221]/20 via-white to-orange-50/40 p-8 sm:p-10">
      <BettingSectionHead eyebrow="CTAs" title={data.strongCta.headline} />
      <ul className="mt-4 space-y-3">
        {data.strongCta.items.map((item) => (
          <li key={item} className="flex gap-3 text-sm font-black text-stone-900 sm:text-[0.9375rem]">
            <span className="text-orange-600">👉</span>
            {item}
          </li>
        ))}
      </ul>
    </section>

    <section>
      <BettingSectionHead eyebrow="Summary" title={data.takeaway.headline} />
      <div className="rounded-2xl border border-stone-200 bg-gradient-to-b from-white to-stone-50/50 p-6 sm:p-8">
        {data.takeaway.paragraphs.map((p) => (
          <p key={p} className="mb-4 last:mb-0 text-sm font-semibold leading-relaxed text-stone-700">
            {p}
          </p>
        ))}
        <div className="rounded-xl border-l-4 border-orange-500 bg-orange-50/50 p-4 text-sm font-semibold text-stone-800">
          👉 {data.takeaway.footnote}
        </div>
      </div>
    </section>

    <section>
      <BettingSectionHead eyebrow="Quick answers" title="FAQs" />
      <div className="rounded-2xl border border-orange-100/90 bg-white p-4 shadow-md sm:p-6">
        <div className="space-y-2 divide-y divide-orange-100/80">
          {data.faqs.map((faq) => (
            <div key={faq.q} className="pt-2 first:pt-0">
              <FAQItem q={faq.q} a={faq.a} color={color} />
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export const CasinoGamesArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-12">
    <section>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <BettingSectionHead eyebrow="Library" title="Articles & guides" subtitle={data.funnelTagline} />
        <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-black text-orange-900">
          {data.articles.length} guides
        </span>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>
    <section className="overflow-hidden rounded-[1.5rem] border border-orange-200/70 bg-gradient-to-br from-stone-900 to-orange-950 p-8 text-white sm:p-10">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em] text-[#FFD221]">{data.editorialPlaybook.leadCtasHeading}</p>
          <h3 className="text-xl font-black sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm text-white/85">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#FFD221]" />
                {c}
              </li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-[#FFD221] px-8 py-4 text-sm font-black text-black shadow-lg transition hover:brightness-105"
        >
          Register free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const CasinoGamesMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;
  return (
    <div className="min-w-0 space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <BettingSectionHead
          eyebrow="Watch & listen"
          title="Videos & podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
        />
        <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-black text-orange-900">{total} featured</span>
      </div>
      {videos.length > 0 ? (
        <section className="min-w-0 rounded-2xl border border-orange-100/90 bg-white/90 p-4 shadow-md sm:p-6">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Online casino education videos"
            items={videos}
            header={<h3 className="text-lg font-black text-stone-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}
      {podcasts.length > 0 ? (
        <section className="min-w-0 rounded-2xl border border-orange-100/90 bg-white/90 p-4 shadow-md sm:p-6">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Online casino podcasts"
            items={podcasts}
            header={<h3 className="text-lg font-black text-stone-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
        </section>
      ) : null}
    </div>
  );
};
